/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React from 'react';
import { BadgeCheck, AlertCircle, Loader2 } from 'lucide-react';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';

/**
 * Confirmation dialog for approving or rejecting a pending registry verification.
 * Requires a rejection reason when action==='reject'.
 * Extracted from AdminOnboardingPanel.jsx (Phase 8 refactor).
 */
const ReviewConfirmDialog = ({
  open,
  action,
  user,
  reason,
  onReasonChange,
  onCancel,
  onConfirm,
  busy,
}) => (
  <Dialog open={open} onOpenChange={(o) => { if (!o) onCancel(); }}>
    <DialogContent className="max-w-md" data-testid="admin-onb-review-dialog">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          {action === 'approve' ? (
            <>
              <BadgeCheck className="h-5 w-5 text-emerald-600" />
              Approve verification?
            </>
          ) : (
            <>
              <AlertCircle className="h-5 w-5 text-destructive" />
              Reject verification?
            </>
          )}
        </DialogTitle>
        <DialogDescription>
          {action === 'approve'
            ? `Approving will grant ${user?.full_name || 'this professional'} access to Tier 3 and full clinical features.`
            : `Rejecting will notify ${user?.full_name || 'the user'} by email. Please provide a clear reason.`}
        </DialogDescription>
      </DialogHeader>

      {action === 'reject' && (
        <div className="mt-2">
          <label className="text-sm font-medium mb-1 block" htmlFor="reject-reason">
            Rejection reason (visible to the user)
          </label>
          <Textarea
            id="reject-reason"
            data-testid="admin-onb-reject-reason"
            rows={4}
            value={reason}
            onChange={(e) => onReasonChange(e.target.value)}
            placeholder="e.g., The uploaded image is not readable. Please re-upload a clear picture of your Albo card."
          />
        </div>
      )}

      <DialogFooter className="gap-2">
        <Button variant="ghost" onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button
          variant={action === 'reject' ? 'destructive' : 'default'}
          onClick={onConfirm}
          disabled={busy || (action === 'reject' && !reason.trim())}
          data-testid="admin-onb-review-confirm"
        >
          {busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
          {action === 'approve' ? 'Approve' : 'Reject'}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export default ReviewConfirmDialog;
